import { Injectable, signal, computed } from '@angular/core';
import {
  GLOBAL_SEARCH_ITEMS,
  GlobalSearchItem,
} from '../core/constants/globalSearch.const';
import { LoginResponse } from '../models/auth.model';
import { AgentRoutes, ModuleConst } from '../core/constants/keys.constant';

@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  private term = signal('');
  private profileType = signal<string>('');
  private permissions = signal<any[]>([]);

  private readonly allowedItems = computed(() =>
    this.filterItemsByPermissions(GLOBAL_SEARCH_ITEMS)
  );

  readonly results = computed(() => {
    const q = this.term().toLowerCase().trim();
    if (!q) return [];

    return this.allowedItems().filter(
      item =>
        item.title.toLowerCase().includes(q) ||
        item.module.toLowerCase().includes(q) ||
        item.keywords.some(k => k.includes(q))
    );
  });

  search(value: string) {
    this.term.set(value);
  }

  clear() {
    this.term.set('');
  }

  setProfileContext(profile: LoginResponse | null): void {
    this.profileType.set(profile?.type ?? '');
    this.permissions.set(profile?.access_role?.permissions ?? []);
  }

  private filterItemsByPermissions(
    items: GlobalSearchItem[]
  ): GlobalSearchItem[] {
    const type = this.profileType();
    const perms = this.permissions();

    if (type !== 'SUB_ADMIN' && type !== 'AGENT') {
      return items;
    }

    const viewableModuleIds = this.buildViewableModuleIds(perms);

    return items
      .filter(item => this.isSearchItemAllowed(item, type, viewableModuleIds))
      .map(item => this.resolveSearchRoute(item, type));
  }

  private isSearchItemAllowed(
    item: GlobalSearchItem,
    profileType: string,
    viewableModuleIds: Set<string>
  ): boolean {
    const moduleId = item.sub_module || item.module;

    if (moduleId === ModuleConst.CRM_Dashboard) {
      return profileType === 'AGENT';
    }

    return viewableModuleIds.has(moduleId);
  }

  private buildViewableModuleIds(permissions: any[]): Set<string> {
    const ids = new Set<string>();

    const walk = (items: any[] | undefined): void => {
      if (!items?.length) {
        return;
      }

      for (const item of items) {
        if (this.hasViewAccess(item)) {
          ids.add(String(item.sidebar_module));
        }

        walk(item.subModules);
        walk(item.subMenuItems);
      }
    };

    walk(permissions);
    return ids;
  }

  private resolveSearchRoute(
    item: GlobalSearchItem,
    profileType: string
  ): GlobalSearchItem {
    const moduleId = item.sub_module || item.module;

    if (profileType === 'AGENT' && moduleId === ModuleConst.CRM_Dashboard) {
      return { ...item, route: AgentRoutes.dashboard };
    }

    if (profileType === 'AGENT' && moduleId === ModuleConst.CRM_ChatListing) {
      return { ...item, route: AgentRoutes.home };
    }

    return item;
  }

  private hasViewAccess(permission: any): boolean {
    return (
      permission?.is_view === true ||
      permission?.is_view === 'true' ||
      permission?.is_view === 1
    );
  }
}
